<template>
  <div v-if="isVisible" class="edit-overlay">
    <div class="edit-overlay-backdrop" @click="close"></div>
    <div class="edit-overlay-content">
      <div class="edit-header">
        <h2>Edit Device</h2>
        <button class="close-button" @click="close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="form-container">
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <span>Loading device data...</span>
        </div>

        <div v-else-if="error" class="error-state">
          <div class="error-message">{{ error }}</div>
          <button class="retry-button" @click="fetchDeviceData">Retry</button>
        </div>

        <div v-else class="form-content">
          <!-- Device Identification Section -->
          <div class="form-section">
            <h3 class="section-title">Device Identification</h3>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Logical Device Number</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="deviceData.logical_device_number"
                    disabled
                    title="Logical Device Number cannot be changed"
                >
              </div>

              <div class="form-group">
                <label class="form-label">Customer Number</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="deviceData.customer_number"
                >
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Device Type</label>
                <div class="toggle-buttons">
                  <button
                      :class="{ active: deviceData.type?.toUpperCase() === 'INTEGRATED' }"
                      @click="selectDeviceType('INTEGRATED')">
                    Integrated
                  </button>
                  <button
                      :class="{ active: deviceData.type === 'ADDON' }"
                      @click="selectDeviceType('ADDON')">
                    Add-On
                  </button>
                  <button
                      :class="{ active: deviceData.type === 'MECANICAL' }"
                      @click="selectDeviceType('MECANICAL')">
                    Mechanical
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Caliber</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="deviceData.caliber"
                >
              </div>
            </div>
          </div>

          <!-- Configuration Section -->
          <div class="form-section">
            <h3 class="section-title">Device Configuration</h3>

            <!-- Protocol Toggle Section -->
            <div class="protocol-section" v-if="deviceData.type !== 'MECANICAL'">
              <div class="protocol-toggles">
                <div class="protocol-toggle-item" :class="{ 'active': hasProtocol('DLMS') }">
                  <div class="toggle-label-container">
                    <label class="form-label">DLMS</label>
                    <div class="toggle-switch">
                      <input
                          type="checkbox"
                          id="dlms-toggle"
                          :checked="hasProtocol('DLMS')"
                          @change="toggleProtocol('DLMS')"
                      >
                      <label for="dlms-toggle"></label>
                    </div>
                  </div>
                </div>

                <div class="protocol-toggle-item" :class="{ 'active': hasProtocol('LORA') }">
                  <div class="toggle-label-container">
                    <label class="form-label">LORA</label>
                    <div class="toggle-switch">
                      <input
                          type="checkbox"
                          id="lora-toggle"
                          :checked="hasProtocol('LORA')"
                          @change="toggleProtocol('LORA')"
                      >
                      <label for="lora-toggle"></label>
                    </div>
                  </div>
                </div>

                <div class="protocol-toggle-item" :class="{ 'active': hasProtocol('CTR') }">
                  <div class="toggle-label-container">
                    <label class="form-label">CTR</label>
                    <div class="toggle-switch">
                      <input
                          type="checkbox"
                          id="ctr-toggle"
                          :checked="hasProtocol('CTR')"
                          @change="toggleProtocol('CTR')"
                      >
                      <label for="ctr-toggle"></label>
                    </div>
                  </div>
                </div>

                <div class="protocol-toggle-item" :class="{ 'active': hasProtocol('WBUS') }">
                  <div class="toggle-label-container">
                    <label class="form-label">WBUS</label>
                    <div class="toggle-switch">
                      <input
                          type="checkbox"
                          id="wbus-toggle"
                          :checked="hasProtocol('WBUS')"
                          @change="toggleProtocol('WBUS')"
                      >
                      <label for="wbus-toggle"></label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- LORA Fields (show when LORA protocol is active) -->
            <div class="form-row" v-if="hasProtocol('LORA')">
              <div class="form-group">
                <label class="form-label">Radio ID</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="deviceData.id_radio"
                >
              </div>

              <div class="form-group">
                <label class="form-label">LORA EUI</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="deviceData.lora_eui"
                >
              </div>
            </div>
          </div>

          <!-- Installation Section -->
          <div class="form-section">
            <h3 class="section-title">Installation & Management</h3>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Installation Date</label>
                <input
                    type="date"
                    class="form-input"
                    v-model="formattedInstallDate"
                >
              </div>

              <div class="form-group">
                <label class="form-label">Distribution Company</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="deviceData.distribution_company"
                >
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">MPID</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="deviceData.mpid"
                    disabled>
                <small class="field-hint">Metering Point ID that this device is attached to</small>
              </div>

              <div class="form-group">
                <label class="form-label">Removal Date</label>
                <input
                    type="date"
                    class="form-input"
                    v-model="formattedRemovalDate"
                >
                <small class="field-hint">Leave blank if device is currently active</small>
              </div>
            </div>
          </div>

          <!-- Security Keys Section - Conditional based on protocol -->
          <div class="form-section" v-if="showSecurityKeysSection">
            <div class="section-header">
              <h3 class="section-title">Security Keys</h3>
              <button class="toggle-button" @click="showSecurityKeys = !showSecurityKeys">
                {{ showSecurityKeys ? 'Hide' : 'Show' }}
              </button>
            </div>

            <div v-if="showSecurityKeys" class="security-keys-content">
              <!-- DLMS Key -->
              <div class="form-row" v-if="hasProtocol('DLMS')">
                <div class="form-group">
                  <label class="form-label">DLMS Key</label>
                  <input
                      type="text"
                      class="form-input"
                      v-model="deviceData.dlmskey"
                  >
                </div>
              </div>

              <!-- LORA Keys -->
              <div class="form-row" v-if="hasProtocol('LORA')">
                <div class="form-group">
                  <label class="form-label">LORA Key</label>
                  <input
                      type="text"
                      class="form-input"
                      v-model="deviceData.lorakey"
                  >
                </div>

                <div class="form-group">
                  <label class="form-label">LORA Network Key</label>
                  <input
                      type="text"
                      class="form-input"
                      v-model="deviceData.lorantwkey"
                  >
                </div>
              </div>

              <!-- WBUS Key -->
              <div class="form-row" v-if="hasProtocol('WBUS')">
                <div class="form-group">
                  <label class="form-label">WBUS Key</label>
                  <input
                      type="text"
                      class="form-input"
                      v-model="deviceData.wbuskey"
                  >
                </div>
              </div>

              <!-- CTR Key -->
              <div class="form-row" v-if="hasProtocol('CTR')">
                <div class="form-group">
                  <label class="form-label">CTR Key</label>
                  <input
                      type="text"
                      class="form-input"
                      v-model="deviceData.ctrkey"
                  >
                </div>
              </div>

              <!-- ICCID — only for DLMS or CTR (NB-IoT SIM) -->
              <div class="form-row" v-if="hasProtocol('DLMS') || hasProtocol('CTR')">
                <div class="form-group">
                  <label class="form-label">ICCID</label>
                  <input
                      type="text"
                      class="form-input"
                      v-model="deviceData.iccid"
                      placeholder="NB-IoT SIM card number (optional)"
                  >
                  <small class="field-hint">Optional — NB-IoT SIM card identifier</small>
                </div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="form-row">
              <div class="form-group">
                <div class="toggle-label-container">
                  <div>
                    <label class="form-label">Export Device Data</label>
                    <small class="field-hint">Enable this to mark the device for data export</small>
                  </div>
                  <div class="toggle-switch">
                    <input
                        type="checkbox"
                        id="export-toggle"
                        v-model="exportStatus"
                        @change="updateExportStatus"
                        :disabled="updatingExportStatus"
                    >
                    <label for="export-toggle"></label>
                  </div>
                </div>
                <div v-if="exportStatusError" class="error-message">
                  {{ exportStatusError }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="edit-actions">
        <button class="action-button" @click="close">Cancel</button>
        <button
            class="action-button submit-button"
            @click="updateDevice"
            :disabled="loading || !hasChanges || !requiredSecurityKeysValid"
        >
          Save Changes
        </button>
        <button
            class="action-button delete-button"
            @click="promptDelete"
            :disabled="loading"
            title="Delete device"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import DeviceApiServices from '@/services/DeviceApiServices';

export default {
  name: 'DeviceDetailsEditOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    deviceId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: '',
      showSecurityKeys: false,
      originalDeviceData: null,
      exportStatus: false,
      originalExportStatus: false,
      updatingExportStatus: false,
      exportStatusError: '',
      deviceData: {
        caliber: '',
        customer_number: '',
        distribution_company: '',
        dlmskey: '',
        id_radio: '',
        installed: '',
        is_integrated: false,
        logical_device_number: '',
        lora_eui: '',
        lorakey: '',
        lorantwkey: '',
        mpid: '',
        protocol: '',
        removed: '',
        tenant_id: '',
        type: '',
        wbuskey: '',
        ctrkey: '',
        iccid: '',   // optional — NB-IoT SIM, only for DLMS/CTR
        addon_id: ''
      }
    };
  },
  computed: {
    formattedInstallDate: {
      get() {
        if (!this.deviceData.installed) return '';
        return this.formatDateForInput(this.deviceData.installed);
      },
      set(value) {
        this.deviceData.installed = value ? new Date(value).toISOString() : '';
      }
    },
    formattedRemovalDate: {
      get() {
        if (!this.deviceData.removed) return '';
        return this.formatDateForInput(this.deviceData.removed);
      },
      set(value) {
        this.deviceData.removed = value ? new Date(value).toISOString() : '';
      }
    },
    hasChanges() {
      if (!this.originalDeviceData) return false;

      const fieldsToCompare = [
        'caliber', 'customer_number', 'distribution_company', 'id_radio',
        'is_integrated', 'lora_eui', 'mpid', 'protocol', 'type',
        'installed', 'removed', 'dlmskey', 'lorakey', 'lorantwkey',
        'wbuskey', 'ctrkey', 'iccid', 'addon_id'
      ];

      const norm = v => (v === null || v === undefined) ? '' : String(v);

      return fieldsToCompare.some(field => {
        if (field === 'installed' || field === 'removed') {
          const original = this.formatDateForInput(this.originalDeviceData[field] || '');
          const current = this.formatDateForInput(this.deviceData[field] || '');
          return original !== current;
        }
        if (field === 'protocol') {
          return norm(this.originalDeviceData[field]) !== norm(this.deviceData[field]);
        }
        if (field === 'type') {
          return norm(this.originalDeviceData[field]).toUpperCase() !== norm(this.deviceData[field]).toUpperCase();
        }
        return norm(this.originalDeviceData[field]) !== norm(this.deviceData[field]);
      });
    },
    showSecurityKeysSection() {
      return this.deviceData.protocol && this.deviceData.protocol !== 'NONE';
    },
    requiredSecurityKeysValid() {
      if (this.deviceData.type === 'MECHANICAL' || !this.deviceData.protocol || this.deviceData.protocol === 'NONE') {
        return true;
      }
      const protocol = this.deviceData.protocol;
      if (protocol.includes('DLMS') && !this.deviceData.dlmskey?.trim()) return false;
      if (protocol.includes('LORA') && !this.deviceData.lorakey?.trim()) return false;
      if (protocol.includes('WBUS') && !this.deviceData.wbuskey?.trim()) return false;
      if (protocol.includes('CTR') && !this.deviceData.ctrkey?.trim()) return false;
      return true;
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal && this.deviceId) {
        this.fetchDeviceData();
      }
    },
    deviceId(newVal) {
      if (newVal && this.isVisible) {
        this.fetchDeviceData();
      }
    }
  },
  mounted() {
    if (this.isVisible && this.deviceId) {
      this.fetchDeviceData();
    }
  },
  methods: {
    formatDateForInput(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    },

    async fetchDeviceData() {
      this.loading = true;
      this.error = '';
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${apiBaseUrl}/devices/${this.deviceId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          throw new Error(`Error fetching device details: ${response.statusText}`);
        }
        const data = await response.json();

        this.exportStatus = data.to_export || false;
        this.originalExportStatus = this.exportStatus;

        const deviceDataWithoutExport = { ...data };
        delete deviceDataWithoutExport.to_export;

        if (deviceDataWithoutExport.type) {
          deviceDataWithoutExport.type = deviceDataWithoutExport.type.toUpperCase();
        }
        if (!deviceDataWithoutExport.protocol) {
          deviceDataWithoutExport.protocol = '';
        }
        // Ensure iccid is always a string
        if (!deviceDataWithoutExport.iccid) {
          deviceDataWithoutExport.iccid = '';
        }

        this.deviceData = deviceDataWithoutExport;
        this.originalDeviceData = { ...deviceDataWithoutExport };
      } catch (error) {
        console.error('Error fetching device details:', error);
        this.error = 'Failed to load device details. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    hasProtocol(protocol) {
      if (!this.deviceData.protocol) return false;
      if (protocol === 'WBUS') {
        return this.deviceData.protocol.includes('WBUS');
      }
      return this.deviceData.protocol.includes(protocol);
    },

    toggleProtocol(protocol) {
      let currentProtocols = this.deviceData.protocol ? this.deviceData.protocol.split('_') : [];

      if (protocol === 'WBUS' && currentProtocols.includes('LORA') && !currentProtocols.includes('WBUS')) {
        this.deviceData.protocol = 'LORA_WBUS';
        return;
      }
      if (protocol === 'LORA' && currentProtocols.includes('WBUS') && !currentProtocols.includes('LORA')) {
        this.deviceData.protocol = 'LORA_WBUS';
        return;
      }

      if (this.hasProtocol(protocol)) {
        if (protocol === 'WBUS' && this.deviceData.protocol === 'LORA_WBUS') {
          this.deviceData.protocol = 'LORA';
        } else if (protocol === 'LORA' && this.deviceData.protocol === 'LORA_WBUS') {
          this.deviceData.protocol = 'WBUS';
        } else {
          this.deviceData.protocol = '';
        }
      } else {
        this.deviceData.protocol = protocol;
      }

      this.$forceUpdate();
    },

    selectDeviceType(selectedType) {
      this.deviceData.type = selectedType;
      this.deviceData.is_integrated = selectedType === 'METER';
      if (selectedType === 'MECHANICAL') {
        this.deviceData.protocol = 'NONE';
      }
    },

    async updateExportStatus() {
      this.updatingExportStatus = true;
      this.exportStatusError = '';
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${apiBaseUrl}/devices/export`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            logical_device_number: this.deviceData.logical_device_number,
            to_export: this.exportStatus
          })
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update export status');
        }
        this.originalExportStatus = this.exportStatus;
        this.$emit('export-status-updated', {
          logical_device_number: this.deviceData.logical_device_number,
          to_export: this.exportStatus
        });
      } catch (error) {
        console.error('Error updating export status:', error);
        this.exportStatusError = 'Failed to update export status. Please try again.';
        this.exportStatus = this.originalExportStatus;
      } finally {
        this.updatingExportStatus = false;
      }
    },

    async updateDevice() {
      if (!this.hasChanges) return;
      if (!this.requiredSecurityKeysValid) {
        this.error = 'Please provide the required security key for the selected protocol.';
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${apiBaseUrl}/devices/${this.deviceId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.deviceData)
        });
        if (!response.ok) {
          throw new Error(`Error updating device: ${response.statusText}`);
        }
        this.$emit('device-updated', this.deviceData);
        this.close();
      } catch (error) {
        console.error('Error updating device:', error);
        this.error = 'Failed to update device details. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async promptDelete() {
      const ldn = this.deviceData.logical_device_number || this.deviceId || '';
      if (!ldn) {
        this.error = 'Missing Logical Device Number.';
        return;
      }
      const input = window.prompt(`Type the Logical Device Number to confirm deletion:\n\n${ldn}`);
      if (input === null) return;
      if (input.trim() !== ldn) {
        this.error = 'Confirmation text does not match the Logical Device Number.';
        return;
      }
      try {
        this.loading = true;
        if (!this.deviceData.removed) {
          await this.markAsRemovedToday(ldn);
        }
        await DeviceApiServices.deleteDevice(ldn);
        this.$emit('device-deleted', { logical_device_number: ldn });
        this.$router.push({ path: '/devices' });
        this.close();
      } catch (e) {
        console.error(e);
        this.error = e?.response?.data?.message || e?.message || 'Failed to delete device.';
      } finally {
        this.loading = false;
      }
    },

    async markAsRemovedToday(ldn) {
      try {
        await DeviceApiServices.removeDevice(ldn);
      } catch (err) {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const resp = await fetch(`${apiBaseUrl}/devices/${encodeURIComponent(ldn)}/remove`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({})
        });
        if (!resp.ok) {
          const t = await resp.text().catch(() => '');
          throw new Error(t || 'Failed to mark device as removed.');
        }
      }
      const todayIso = new Date().toISOString();
      this.deviceData.removed = todayIso;
      if (this.originalDeviceData) this.originalDeviceData.removed = todayIso;
    },

    close() {
      this.deviceData = {
        caliber: '',
        customer_number: '',
        distribution_company: '',
        dlmskey: '',
        id_radio: '',
        installed: '',
        is_integrated: false,
        logical_device_number: '',
        lora_eui: '',
        lorakey: '',
        lorantwkey: '',
        mpid: '',
        protocol: '',
        removed: '',
        tenant_id: '',
        type: '',
        wbuskey: '',
        ctrkey: '',
        iccid: '',
        addon_id: ''
      };
      this.originalDeviceData = null;
      this.showSecurityKeys = false;
      this.error = '';
      this.exportStatus = false;
      this.originalExportStatus = false;
      this.exportStatusError = '';
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Overlay */
.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.edit-overlay-content {
  position: relative;
  width: 700px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  background-color: rgba(50, 50, 50, 0.2);
  backdrop-filter: blur(16px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.edit-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: rgba(255, 255, 255, 0.9);
}

/* Form container */
.form-container {
  position: relative;
  min-height: 200px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  gap: 16px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Form sections */
.form-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.form-section:last-of-type {
  border-bottom: none;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 16px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.toggle-button {
  background: none;
  border: none;
  color: #4fc3f7;
  cursor: pointer;
  font-size: 12px;
  transition: opacity 0.2s;
}

.toggle-button:hover {
  opacity: 0.8;
}

/* Form rows and groups */
.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 16px;
}

.form-group {
  flex: 1;
  min-width: 200px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.7);
}

.form-input {
  width: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  padding: 8px 12px;
  font-size: 13px;
  transition: border-color 0.2s, background-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: rgba(79, 195, 247, 0.5);
  background-color: rgba(79, 195, 247, 0.05);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

select.form-input {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
}

.field-hint {
  display: block;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
}

/* Toggle switch */
.toggle-label-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-switch {
  position: relative;
  width: 46px;
  height: 22px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch label {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.1);
  transition: .4s;
  border-radius: 34px;
}

.toggle-switch label:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

.toggle-switch input:checked + label {
  background-color: rgba(79, 195, 247, 0.5);
}

.toggle-switch input:checked + label:before {
  transform: translateX(24px);
}

/* Protocol Toggle Section */
.protocol-section {
  margin-bottom: 16px;
}

.protocol-toggles {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
}

.protocol-toggle-item {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
  flex: 1;
  transition: background-color 0.2s, border-color 0.2s;
  border: 1px solid transparent;
}

.protocol-toggle-item.active {
  background-color: rgba(79, 195, 247, 0.1);
  border-color: rgba(79, 195, 247, 0.3);
}

.protocol-toggle-item .toggle-label-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Security keys section */
.security-keys-content {
  animation: fadeIn 0.3s ease forwards;
}

/* Action buttons */
.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79, 195, 247, 0.2);
}

.submit-button:hover:not(:disabled) {
  background-color: rgba(79, 195, 247, 0.3);
}

.delete-button {
  color: #ff8a80;
  border: 1px solid rgba(255, 138, 128, 0.4);
}

.delete-button:hover:not(:disabled) {
  background-color: rgba(255, 138, 128, 0.12);
}

/* Toggle Buttons for Device Type */
.toggle-buttons {
  display: flex;
  gap: 8px;
}

.toggle-buttons button {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 4px;
  color: white;
}

.toggle-buttons button.active {
  background-color: rgba(79, 195, 247, 0.3);
  border-color: rgba(79, 195, 247, 0.5);
}

.toggle-buttons button:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .form-row {
    flex-direction: column;
    gap: 16px;
  }
  .form-group {
    min-width: 100%;
  }
  .edit-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .action-button {
    width: 100%;
  }
  .protocol-toggles {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
